import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import { VARIANT_ENUM } from '@/components/@base/button/type';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Section from '@/components/app/product/components/@shared/section';
import Highlights from '@/components/app/product/components/highlights';
import Props from '@/components/app/product/components/support/type';
import { useGetAddOnsInfo } from '@/components/app/product/services/hooks';
import Actions from '@/components/shared/components/cards/@shared/actions';
import PreviewButton from '@/components/shared/components/preview-button';

import { DATA_CY_PRODUCT_SUPPORT } from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';
import { DASHBOARD_BASE_URL } from '@/constant/routes';

const { product: productLocalization } = pageLevelLocalization;

const defaultHighlights = [
  productLocalization.defaultHighlight1,
  productLocalization.defaultHighlight2,
  productLocalization.defaultHighlight3,
];

const oneYearSubscriptionHighlight = [
  productLocalization.oneYearSubscriptionHighlight1,
  productLocalization.oneYearSubscriptionHighlight2,
  productLocalization.oneYearSubscriptionHighlight3,
  productLocalization.oneYearSubscriptionHighlight4,
  productLocalization.oneYearSubscriptionHighlight5,
];

export default function Support({ slug, productId, isSubscription }: Props) {
  const { data: addOnsData } = useGetAddOnsInfo({ slug });
  const isBuyer = !!(addOnsData && addOnsData.isProductBuyer);
  const highlights = isSubscription ? oneYearSubscriptionHighlight : defaultHighlights;

  return (
    <Section data-cy={DATA_CY_PRODUCT_SUPPORT} className='mx-0! p-1.5!'>
      <Container
        center
        className='h-[96px] w-full items-end rounded-[5px] bg-linear-to-b from-[#6097F3]/10 to-[#6097f300]'
      >
        <XImage
          src={imagesList.icons.supportIcon}
          alt={productLocalization.support}
          className='h-[54px] w-[54px] rounded-full bg-[#6097F3] p-[15px]'
        />
      </Container>

      <Text
        bold
        className='mx-auto mt-[18px] mb-[47px] max-w-[220px] text-center leading-7 text-[#424244]'
        size={SIZE_ENUM.LG}
      >
        {productLocalization.buyProductToGetSupport}
      </Text>

      <Container className='grid grid-cols-1 gap-8 px-4 md:grid-cols-2 md:px-[140px]'>
        <Container className='grid-cols-1'>
          <Highlights highlights={highlights} noHeader className='border-none!' />

          <Container className='mt-7 flex flex-col rounded-lg bg-[#878F9B] p-4 text-[#F7F8F9]'>
            <XImage src={imagesList.icons.ticketIcon} alt={productLocalization.support} />
            <Text>{productLocalization.contactVendorByTicket}</Text>
          </Container>
        </Container>
        <Container center className='relative col-span-1 w-full md:max-w-[307px]'>
          <XImage
            src={imagesList.images.ticketGuide}
            alt={productLocalization.support}
            className='object-contain'
            // fill
            width={242}
            height={253}
          />
          <Container
            center
            className='absolute bottom-[66px] z-10 h-[57px] w-full max-w-[307px] justify-between rounded-[10px] bg-[#FFFFFF] px-6 shadow-[0px_20px_60px_-15px_#00000026]'
          >
            <Container center className='gap-4'>
              <XImage
                src={imagesList.icons.blackTicketIcon}
                alt={productLocalization.ticketSupport}
                width={19.8}
                height={20.75}
              />
              <Text bold className='text-base text-[#5B5C60]'>
                {productLocalization.ticketSupport}
              </Text>
            </Container>
            <XImage
              src={imagesList.icons.addOrange}
              alt={productLocalization.ticketSupport}
              width={15}
              height={15}
            />
          </Container>
        </Container>
      </Container>
      <Container center className='my-[60px] w-full gap-4 px-4 md:px-[140px]'>
        {isBuyer ? (
          <>
            <PreviewButton
              className='h-[55px]'
              hasIcon
              slug={slug}
              variant={VARIANT_ENUM.SOUL}
            />

            <a
              className='h-[55px] flex-1'
              target='_blank'
              rel='noopener noreferrer'
              href={`${DASHBOARD_BASE_URL}/panel/tickets/new`}
            >
              <Button
                size={SIZE_ENUM.SM}
                className='h-full w-full gap-2.5 p-0 hover:bg-[#EB8800]'
                Icon={
                  <XImage
                    src={imagesList.icons.ticketIcon}
                    alt={productLocalization.support}
                    width={15}
                    height={15}
                  />
                }
              >
                <Text bold>{productLocalization.sendTicket}</Text>
              </Button>
            </a>
          </>
        ) : (
          <Actions
            isFree={addOnsData?.price === 0}
            className='h-[55px]'
            hasIcon
            slug={slug}
            productId={productId}
            previewButtonVariant={VARIANT_ENUM.SOUL}
          />
        )}
      </Container>
    </Section>
  );
}
